import React from 'react';
import cn from 'classnames';
import DocumentationRequirementBulletItem from './DocumentationRequirementBulletItem';
import SectionTitle from '../Shared/Tailwind/SectionTitle';
import SampleDocumentModal from './SampleDocumentModal';
import { getSamplePageByPath, getSamplePageTitleByPath, SAMPLE_DOCUMENT_FILE_NAME, SAMPLE_PAGE_PATH } from './samplePageRegistry';
import { requirementSections } from './documentRequirements';

const styles = {
  card: 'self-stretch p-10 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-cyan inline-flex flex-col justify-start items-start',
  title: 'justify-start text-blue text-3xl font-normal font-serif leading-10',
  introSection: 'self-stretch pt-6',
  introText: 'self-stretch text-left text-gray-800 text-lg font-normal leading-7',
  sectionWrap: 'self-stretch pt-8',
  sectionList: 'self-stretch flex flex-col gap-6',
  sectionCard: 'self-stretch p-6 rounded-lg outline outline-1 outline-offset-[-1px] outline-brand-border',
  sectionHeading: 'text-xl leading-8 text-brand-navy font-extrabold',
  helperText: 'pt-4 text-left text-gray-800 text-base font-normal leading-6',
  bulletList: 'pt-4 flex flex-col gap-3',
};

const DocumentationRequirement = () => {
  const [activeSampleFileName, setActiveSampleFileName] = React.useState<string | null>(null);
  const ActiveSamplePage = getSamplePageByPath(SAMPLE_PAGE_PATH, activeSampleFileName ?? SAMPLE_DOCUMENT_FILE_NAME);
  const activeSampleTitle = React.useMemo(
    () => getSamplePageTitleByPath(SAMPLE_PAGE_PATH, activeSampleFileName ?? SAMPLE_DOCUMENT_FILE_NAME),
    [activeSampleFileName]
  );
  const activeInstructions = React.useMemo(() => {
    if (!activeSampleFileName) {
      return undefined;
    }

    return requirementSections.flatMap((section) => section.items).find((item) => item.sampleFileName === activeSampleFileName)?.instructions;
  }, [activeSampleFileName]);
  
  const activeContactInfo = React.useMemo(() => {
    if (!activeSampleFileName) {
      return undefined;
    }

    return requirementSections.flatMap((section) => section.items).find((item) => item.sampleFileName === activeSampleFileName)?.contactInfo;
  }, [activeSampleFileName]);

  return (
    <div className={cn(styles.card)}>
      <SectionTitle className={styles.title}>Documentation Requirements</SectionTitle>
      <div className={cn(styles.introSection)}>
        <div className={cn(styles.introText)}>
          Before you begin, please review the documentation requirements for each type of request. This form can be saved for up to 90 days to allow
          you to gather the necessary information. After 90 days, the form will expire and you will be required to start a new request.
        </div>
      </div>
      <div className={cn(styles.sectionWrap)}>
        <div className={cn(styles.sectionList)}>
          {requirementSections.map((section) => (
            <div key={section.title} className={cn(styles.sectionCard)}>
              <SectionTitle className={styles.sectionHeading}>{section.title}</SectionTitle>
              <div className={cn(styles.helperText)}>Please prepare the following documents:</div>
              <div className={cn(styles.bulletList)}>
                {section.items.map((item) => (
                  <DocumentationRequirementBulletItem
                    key={item.documentId}
                    label={item.title}
                    description={item.description}
                    note={item?.note}
                    viewSampleText={item.sampleText}
                    onViewSampleClick={item.sampleFileName ? () => setActiveSampleFileName(item.sampleFileName) : undefined}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <SampleDocumentModal
        isOpen={activeSampleFileName !== null}
        onClose={() => setActiveSampleFileName(null)}
        page={activeSampleFileName ? <ActiveSamplePage /> : null}
        title={activeSampleTitle}
        instructionalText={activeInstructions}
        contactInfo={activeContactInfo}
      />
    </div>
  );
};

export default DocumentationRequirement;
